import * as THREE from "three";

const MAX_FLOWERS = 80;

const PETAL_PALETTES = [
  { petal: 0xf7a8c4, core: 0xffd97a },
  { petal: 0xc3a6ff, core: 0xfff1b8 },
  { petal: 0xffc49b, core: 0xff9d6c },
  { petal: 0xfff1e6, core: 0xffcf5c },
];

const easeOutBack = (k) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(k - 1, 3) + c1 * Math.pow(k - 1, 2);
};

class FlowerSpawner {
  constructor({ scene, onPlanted }) {
    this.scene = scene;
    this.onPlanted = onPlanted;
    this.flowers = [];
    this.plantedTotal = 0;

    this.stemMat = new THREE.MeshStandardMaterial({
      color: 0x3f7d54,
      roughness: 0.9,
    });
    this.leafMat = new THREE.MeshStandardMaterial({
      color: 0x4f9163,
      roughness: 0.9,
    });
    this.petalMats = PETAL_PALETTES.map(
      ({ petal, core }) =>
        new THREE.MeshStandardMaterial({
          color: petal,
          emissive: petal,
          emissiveIntensity: 0.22,
          roughness: 0.55,
        })
    );
    this.coreMats = PETAL_PALETTES.map(
      ({ core }) =>
        new THREE.MeshStandardMaterial({
          color: core,
          emissive: core,
          emissiveIntensity: 0.45,
          roughness: 0.4,
        })
    );

    this.stemGeo = new THREE.CylinderGeometry(0.02, 0.038, 1, 6);
    this.petalGeo = new THREE.SphereGeometry(0.14, 10, 8);
    this.coreGeo = new THREE.SphereGeometry(0.105, 12, 10);
    this.leafGeo = new THREE.SphereGeometry(0.1, 8, 6);

    this.upVector = new THREE.Vector3(0, 1, 0);
    this.quat = new THREE.Quaternion();
    this.spinQuat = new THREE.Quaternion();
  }

  buildFlowerMesh({ height = 0.6, petals = 6, paletteIndex = 0, headScale = 1 } = {}) {
    const group = new THREE.Group();
    const petalMat = this.petalMats[paletteIndex % this.petalMats.length];
    const coreMat = this.coreMats[paletteIndex % this.coreMats.length];

    const stem = new THREE.Mesh(this.stemGeo, this.stemMat);
    stem.scale.y = height;
    stem.position.y = height / 2;
    group.add(stem);

    [-0.35, -0.55].forEach((offset, i) => {
      const leaf = new THREE.Mesh(this.leafGeo, this.leafMat);
      leaf.position.y = height * (1 + offset);
      leaf.scale.set(1.6, 0.28, 0.7);
      leaf.rotation.y = i === 0 ? 0.6 : 2.4;
      leaf.rotation.z = -0.5;
      group.add(leaf);
    });

    const head = new THREE.Group();
    head.position.y = height;

    const core = new THREE.Mesh(this.coreGeo, coreMat);
    core.scale.setScalar(headScale);
    core.castShadow = true;
    head.add(core);

    for (let i = 0; i < petals; i += 1) {
      const arm = new THREE.Group();
      arm.rotation.y = (i / petals) * Math.PI * 2 + Math.random() * 0.2;
      const petal = new THREE.Mesh(this.petalGeo, petalMat);
      petal.position.x = 0.17 * headScale;
      petal.scale.set(
        1.5 * headScale,
        0.38 * headScale,
        0.85 * headScale
      );
      petal.rotation.z = -0.22;
      petal.castShadow = true;
      arm.add(petal);
      head.add(arm);
    }

    group.add(head);
    return { group, head };
  }

  spawnFlower(point, normal, options = {}) {
    if (this.flowers.length >= MAX_FLOWERS) {
      const oldest = this.flowers.find((f) => !f.dying);
      if (oldest) oldest.dying = true;
    }

    const paletteIndex = Math.floor(Math.random() * PETAL_PALETTES.length);
    const height = options.height ?? 0.35 + Math.random() * 0.65;
    const headScale = options.headScale ?? 0.75 + Math.random() * 0.55;
    const { group, head } = this.buildFlowerMesh({
      height,
      paletteIndex,
      headScale,
    });

    const n = normal.clone().normalize();
    if (n.dot(this.upVector) < -0.9) n.copy(this.upVector);
    this.quat.setFromUnitVectors(this.upVector, n);
    this.spinQuat.setFromAxisAngle(n, Math.random() * Math.PI * 2);
    group.quaternion.copy(this.quat).multiply(this.spinQuat);
    group.position.copy(point).addScaledVector(n, 0.02);

    const record = {
      group,
      head,
      growT: -(options.delay ?? 0),
      growDur: options.growDur ?? 0.55 + Math.random() * 0.35,
      maxScale: options.maxScale ?? 1,
      phase: Math.random() * Math.PI * 2,
      dying: false,
      dieT: 0,
    };

    group.scale.setScalar(0.001);
    this.scene.add(group);
    this.flowers.push(record);
    this.plantedTotal += 1;
    if (this.onPlanted) this.onPlanted(this.plantedTotal);
    return record;
  }

  spawnBurst(center, count = 10) {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.15 + Math.random() * 0.95;
      const point = center
        .clone()
        .add(
          new THREE.Vector3(
            Math.cos(angle) * radius,
            0.05,
            Math.sin(angle) * radius
          )
        );
      this.spawnFlower(point, this.upVector, {
        delay: i * 0.055,
        maxScale: 0.5 + Math.random() * 0.45,
        growDur: 0.4 + Math.random() * 0.3,
      });
    }
  }

  update(dt, elapsed) {
    for (let i = this.flowers.length - 1; i >= 0; i -= 1) {
      const f = this.flowers[i];
      if (f.dying) {
        f.dieT += dt / 0.3;
        const s = Math.max(0.001, f.maxScale * (1 - f.dieT));
        f.group.scale.setScalar(s);
        if (f.dieT >= 1) {
          this.scene.remove(f.group);
          this.flowers.splice(i, 1);
        }
        continue;
      }
      f.growT += dt;
      const k = f.growT / f.growDur;
      if (k <= 0) continue;
      const eased = easeOutBack(Math.min(k, 1)) * f.maxScale;
      f.group.scale.setScalar(Math.max(eased, 0.001));
      f.head.rotation.x = Math.sin(elapsed * 1.3 + f.phase) * 0.085;
      f.head.rotation.z = Math.cos(elapsed * 1.1 + f.phase) * 0.07;
    }
  }

  clearAll() {
    this.flowers.forEach((f) => this.scene.remove(f.group));
    this.flowers = [];
  }

  dispose() {
    this.clearAll();
    [
      this.stemGeo,
      this.petalGeo,
      this.coreGeo,
      this.leafGeo,
      ...this.petalMats,
      ...this.coreMats,
      this.stemMat,
      this.leafMat,
    ].forEach((item) => item.dispose());
  }
}

export { FlowerSpawner, PETAL_PALETTES };
export default FlowerSpawner;
